import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

// TODO (daniel) what is with this

export enum CapacityByRoleType {
  PER_COURSE = 'Pro Kurs',
  PER_DAY = 'Pro Schule',
}

export const CAPACITY_ALL_COURSES = 'ALL_COURSES';

/**
 * Filters the given definitions for those that have a reason
 * @param definition List of CapacityPerRole[] or CapacityPerCourse[] to filter
 * @returns The filtered list
 */
export function filterWithoutReason<T extends { reason: Maybe<string> }>(definition: Maybe<T[]>) {
  return (definition ?? []).filter(c => c.reason && c.reason.length > 0);
}

/**
 * @entity CapacityPerRole
 */
@Entity()
export class CapacityPerRole extends AdornisEntity {
  public static override _class = 'CapacityPerRole';

  public static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @Field(type => String)
  public roleId: Maybe<string>;

  @Field(type => Int)
  public needed: Maybe<number>;

  @Field(type => String)
  public type: Maybe<CapacityByRoleType>;

  @Field(type => String)
  public courseClass: Maybe<string>;

  @Field(type => String)
  public reason: Maybe<string>;
}

/**
 * Utility function to push a new CapacityPerRole to a list
 * @param list Current list of CapacityPerRole
 * @param ent Optional CapacityPerRole to push, otherwise a new one is created
 * @returns The new list with the new CapacityPerRole
 */
export function pushNewCapacityByRole(list: Maybe<CapacityPerRole[]>, ent?: CapacityPerRole) {
  list = list ?? [];
  list.push(ent ?? new CapacityPerRole({}));
  return list;
}

/**
 * Searches for the capacity by role (and optional course class) in the given definitions. The precedence is given by the order of the definitions.
 * @param definitions List of CapacityPerRole[] to search in
 * @param roleId Role to search for
 * @param courseClass Optional course class to search for
 * @returns The found capacity or null
 */
export function getCapacityByRole(definitions: Array<Maybe<CapacityPerRole[]>>, roleId: string, courseClass?: string) {
  const existing = definitions.filter(c => c?.length) as CapacityPerRole[][];

  for (const list of existing) {
    const capacity = list.find(
      c =>
        c.roleId === roleId &&
        (courseClass ? c.courseClass === courseClass || c.courseClass === CAPACITY_ALL_COURSES : true),
    );
    if (capacity) return capacity;
  }

  return null;
}

/**
 * @entity CapacityPerCourse
 */
@Entity()
export class CapacityPerCourse extends AdornisEntity {
  public static override _class = 'CapacityPerCourse';

  public static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @Field(type => String)
  public courseClass: Maybe<string>;

  @Field(type => Int)
  public duration: Maybe<number>;

  @Field(type => Int)
  public perWeek: Maybe<number>;

  @Field(type => Int)
  public os: Maybe<number>;

  @Field(type => Int)
  public us: Maybe<number>;

  @Field(type => String)
  public reason: Maybe<string>;
}

/**
 * Utility function to push a new CapacityPerCourse to a list
 * @param list Current list of CapacityPerCourse
 * @param ent Optional CapacityPerCourse to push, otherwise a new one is created
 * @returns The new list with the new CapacityPerCourse
 */
export function pushNewCapacityPerCourse(list: Maybe<CapacityPerCourse[]>, ent?: CapacityPerCourse) {
  list = list ?? [];
  list.push(ent ?? new CapacityPerCourse({}));
  return list;
}

/**
 * Searches for the capacity by course class in the given definitions. The precedence is given by the order of the definitions.
 * @param definitions List of CapacityPerCourse[] to search in
 * @param courseClass Course class to search for
 * @param hasField Field to check for. If it is not set continues to the next definition
 * @returns The found capacity or null
 */
export function getCapacityByCourse(
  definitions: Array<Maybe<CapacityPerCourse[]>>,
  courseClass: string,
  hasField: 'duration' | 'perWeek' | 'os' | 'us',
) {
  const existing = definitions.filter(c => c?.length) as CapacityPerCourse[][];

  for (const list of existing) {
    const capacity = list.find(
      c => (c.courseClass === courseClass || c.courseClass === CAPACITY_ALL_COURSES) && !!c[hasField],
    );
    if (capacity) return capacity;
  }

  return null;
}
